'use client';

import { Monitor, Smartphone, Columns } from 'lucide-react';
import styles from './ReportTabs.module.css';

export type ReportTabView = 'desktop' | 'mobile' | 'compare';

interface ReportTabsProps {
  activeTab: ReportTabView;
  onTabChange: (tab: ReportTabView) => void;
  hasDesktop?: boolean;
  hasMobile?: boolean;
  desktopCount?: number;
  mobileCount?: number;
  locale?: string;
}

export function ReportTabs({
  activeTab,
  onTabChange,
  hasDesktop = true,
  hasMobile = true,
  desktopCount,
  mobileCount,
  locale = 'en',
}: ReportTabsProps) {
  const showCompare = hasDesktop && hasMobile;

  const i18n = {
    en: {
      desktop: 'Desktop',
      mobile: 'Mobile',
      compare: 'Compare',
      issues: 'issues',
    },
    es: {
      desktop: 'Escritorio',
      mobile: 'Movil',
      compare: 'Comparar',
      issues: 'problemas',
    },
  };

  const t = i18n[locale as 'en' | 'es'] || i18n.en;

  return (
    <div className={styles.container}>
      <nav className={styles.tabBar} role="tablist" aria-label="Report view tabs">
        {hasDesktop && (
          <button
            role="tab"
            aria-selected={activeTab === 'desktop'}
            aria-controls="panel-desktop"
            id="tab-desktop"
            className={`${styles.tab} ${activeTab === 'desktop' ? styles.active : ''}`}
            onClick={() => onTabChange('desktop')}
          >
            <Monitor size={18} className={styles.tabIcon} />
            <span className={styles.tabLabel}>{t.desktop}</span>
            {desktopCount !== undefined && (
              <span className={styles.tabCount}>{desktopCount}</span>
            )}
          </button>
        )}

        {hasMobile && (
          <button
            role="tab"
            aria-selected={activeTab === 'mobile'}
            aria-controls="panel-mobile"
            id="tab-mobile"
            className={`${styles.tab} ${activeTab === 'mobile' ? styles.active : ''}`}
            onClick={() => onTabChange('mobile')}
          >
            <Smartphone size={18} className={styles.tabIcon} />
            <span className={styles.tabLabel}>{t.mobile}</span>
            {mobileCount !== undefined && (
              <span className={styles.tabCount}>{mobileCount}</span>
            )}
          </button>
        )}

        {showCompare && (
          <button
            role="tab"
            aria-selected={activeTab === 'compare'}
            aria-controls="panel-compare"
            id="tab-compare"
            className={`${styles.tab} ${activeTab === 'compare' ? styles.active : ''}`}
            onClick={() => onTabChange('compare')}
          >
            <Columns size={18} className={styles.tabIcon} />
            <span className={styles.tabLabel}>{t.compare}</span>
          </button>
        )}
      </nav>
    </div>
  );
}

export default ReportTabs;
